import { Fragment, useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { SortableList } from "@/components/dnd-sortable/dnd-sortable-list";
import { arrayMove } from "@dnd-kit/sortable";
import useProfile from "@/hooks/use-profile";
import { useDrawer } from "@/app/shared/drawer-views/use-drawer";
import { useAdvanceFeatures } from "@/hooks/use-advance-features";
import { EFeatureMode } from "@/utilities/types/AdvanceFeature";
import { Roles } from "@/utilities/types/Users";
import {
  MenuItemsType,
  carbonMenuItems,
} from "@/layouts/carbon/carbon-menu-items";
import { TopLevelMenuItem } from "./components/TopLevelMenuItem";
import {
  pathExistsInMenu,
  shouldShowMenuItem,
} from "./components/PermissionUtils";
import useApi from "@/hooks/useApi";
import AdCreditRepository from "@/utilities/repositories/AdCredit";

const sidebarLdpAccountIds = [
  "dashboard",
  "advertising",
  "allLeads",
  "project-landing-page",
  "follow-up",
  "websiteSetting",
  "buy-domain",
];

export function CarbonSidebarMenu() {
  const pathname = useLocation()?.pathname;
  const { profile } = useProfile();
  const [expandedMenus, setExpandedMenus] = useState<string[]>([]);
  const [items, setItems] = useState(
    carbonMenuItems.filter((item) => !item.advanceFeatureType)
  );
  const { dynamicLayoutMode } = useAdvanceFeatures();
  const { request: getAdCreditInfo, response: adCreditInfo } = useApi({
    request: AdCreditRepository.getAdCreditInfoGeneral,
  });

  const toggleMenu = (menuId: string) => {
    setExpandedMenus((prev) =>
      prev.includes(menuId)
        ? prev.filter((id) => id !== menuId)
        : [...prev, menuId]
    );
  };

  function handleChange(values: { item: MenuItemsType; newIndex: number }) {
    const oldIndex = items.findIndex((item) => item.id === values.item.id);
    setItems((items) => arrayMove(items, oldIndex, values.newIndex));
  }

  const currentRole = profile?.role;
  const userFeatures = profile?.userFeatures;

  // Fetch ad credit info for AGENCY users
  useEffect(() => {
    if (profile?.role === Roles.AGENCY) {
      getAdCreditInfo();
    }
  }, [profile?.role, getAdCreditInfo]);

  useEffect(() => {
    if (profile && profile.role === Roles.ADMIN) {
      // SA has all permission of advance filter
      setItems(carbonMenuItems);
      return;
    }

    const isLDPAccount = profile?.pricingPlans === "NAVISVIP"; // TODO: For ldp demo, please remove this and check again when package module is ready

    const ldpMenuItems = carbonMenuItems.filter((item) =>
      sidebarLdpAccountIds.includes(item.id)
    );

    if (isLDPAccount) {
      setItems(ldpMenuItems);
      return;
    }

    if (!profile?.userFeatures?.length) return;

    if (profile.role === Roles.ADMIN) {
      setItems(carbonMenuItems);
      return;
    }

    let updatedSidebarMenu = carbonMenuItems.filter((menuItem) => {
      // Hide Ad Credits menu if no adCreditData available for AGENCY users

      if (!menuItem.advanceFeatureType) {
        // The menu isn't advance feature
        return true;
      }

      return profile?.userFeatures?.some(
        (feat) =>
          feat.enabled &&
          feat.feature.enabled &&
          feat.feature.type === menuItem.advanceFeatureType
      );
    });

    updatedSidebarMenu = updatedSidebarMenu.map((menuItem) => {
      if (menuItem.id === "agentLocation") {
        // Renaming tab menu
        const isAdvance = dynamicLayoutMode === EFeatureMode.Advance;

        return {
          ...menuItem,
          name: isAdvance ? "My Layout" : "All Districts",
          title: isAdvance ? "My Layout" : "All Districts",
        };
      } else {
        return menuItem;
      }
    });

    setItems(updatedSidebarMenu);
  }, [
    profile?.userFeatures,
    dynamicLayoutMode,
    profile?.role,
    profile?.adRerportUrl,
    profile,
    adCreditInfo?.data?.adCreditData,
  ]);

  // Update expanded menus when pathname changes
  useEffect(() => {
    // Only auto-expand menus when the page first loads
    // or when pathname changes and wasn't synchronized before
    if (expandedMenus.length === 0) {
      items.forEach((item) => {
        if (pathExistsInMenu(item, pathname)) {
          // Find all parent menus that need to be expanded
          const menuId = item.id;
          setExpandedMenus((prev) => [...prev, menuId]);

          // Also check second level menu items
          if (item.subMenuItems) {
            item.subMenuItems.forEach((menuItem) => {
              if (pathExistsInMenu(menuItem, pathname)) {
                const menuItemId = `${item.id}-${menuItem.name}`;
                setExpandedMenus((prev) => [...prev, menuItemId]);
              }
            });
          }
        }
      });
    }
  }, [pathname, items, expandedMenus.length]); // Remove expandedMenus from dependencies to prevent re-running when menus toggle

  const { closeDrawer } = useDrawer();

  return (
    <div className="mb-auto">
      <ul className="pb-12">
        <SortableList items={items} onChange={handleChange}>
          {items.map((item, index) => {
            if (!currentRole) return null;
            if (
              item.id === "advertising" &&
              item.subMenuItems?.some((item) => item.id === "ad-report") &&
              !profile?.adRerportUrl
            ) {
              item.subMenuItems = item.subMenuItems.filter(
                (_item) => _item.id !== "ad-report" && _item.id !== "cpc-insights"
              );
            }

            // Check if the user has permission to see this menu item or any of its children
            if (!shouldShowMenuItem(currentRole, userFeatures, item))
              return null;

            return (
              <Fragment key={`sortable-menu-${item.name}-${index}`}>
                <TopLevelMenuItem
                  item={item}
                  pathname={pathname}
                  expandedMenus={expandedMenus}
                  toggleMenu={toggleMenu}
                  closeDrawer={closeDrawer}
                  currentRole={currentRole}
                  userFeatures={userFeatures}
                  adCreditInfo={adCreditInfo?.data}
                />
              </Fragment>
            );
          })}
        </SortableList>
      </ul>
    </div>
  );
}
